/*
 * Decléor — Cart truth reconciler (free-gift desync safety net)
 * ---------------------------------------------------------------------------
 * Fixes a race with the "Dr Free Gift" app. When you remove your last PAID product,
 * the app silently removes the auto-added free gift SERVER-SIDE. But the theme had
 * optimistically kept item_count = 1 (the gift), so its CartItems controller re-renders
 * the line-items section (server now returns 0 items → blank body) via showForm(), while
 * the count badge, the header count, the in-cart announce bar and the empty-state all still
 * believe there is 1 item. Result: a stuck cart — "1" in the title, blank body, 0,00 €,
 * no empty state. (Symptom the merchant saw: "je supprime tous les produits, le panier ne
 * se met pas à vide, ni le badge".)
 *
 * This is a pure DISPLAY reconciler — it NEVER mutates the cart. On the theme's own cart
 * events it reads the AUTHORITATIVE /cart.js (debounced, and again a beat later to catch the
 * app's late server-side removal). If the real cart disagrees with what the UI shows
 * (empty-state visibility or the badge number), it re-feeds the fresh cart through the theme's
 * own events:
 *   • theme:cart:change  → updates the drawer badge (cart-count-badge.js), header count/price
 *                          (theme Totals), announce bar, reward ladder and the real-total fixer;
 *                          it also sets the theme CartItems' this.cart = fresh + stale = true.
 *   • theme:cart:reload  → makes that controller run loadHTML() against the now-fresh cart, so
 *                          item_count 0 → showEmpty() (and a non-empty cart re-renders correctly).
 *
 * Idempotent + self-terminating: once UI == truth there is no mismatch, so it stops. A guard
 * flag prevents our own re-dispatch from re-triggering the check (no loop). No-ops when the cart
 * routes are unavailable. Only ACTS on a genuine desync, so the normal add/remove flow is untouched.
 */
(function () {
  'use strict';

  var CART_URL = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  var selfDispatch = false;   // true while we re-feed our own events → ignore the echo
  var timers = [];

  function clearTimers() {
    for (var i = 0; i < timers.length; i++) { clearTimeout(timers[i]); }
    timers = [];
  }

  // The theme's empty state is [data-cart-empty]; it carries .cart--hidden when NOT shown.
  function emptyStateShown() {
    var es = document.querySelector('[data-cart-empty]');
    return !!es && !es.classList.contains('cart--hidden');
  }

  // Drawer title count badge (server-rendered, kept in sync by cart-count-badge.js).
  function badgeCount() {
    var b = document.querySelector('[data-cart-badge]');
    if (!b) { return null; }
    var n = parseInt(b.textContent, 10);
    return isNaN(n) ? null : n;
  }

  function reconcile(cart) {
    if (!cart || typeof cart.item_count !== 'number') { return; }
    var shouldBeEmpty = cart.item_count <= 0;
    var badge = badgeCount();
    var mismatch =
      (shouldBeEmpty !== emptyStateShown()) ||      // truth says empty but UI shows a (blank) form, or vice-versa
      (badge !== null && badge !== cart.item_count); // badge out of sync with the real count

    if (!mismatch) { return; }

    selfDispatch = true;
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    setTimeout(function () { selfDispatch = false; }, 0); // release after the synchronous listeners have run
  }

  /* clearTimers() ne tue que les minuteurs, pas les fetch déjà partis. Sans ce compteur, une sonde
     lente appartenant à une mutation précédente pouvait revenir APRÈS une nouvelle mutation et
     réconcilier l'interface contre un panier périmé. Une sonde ne parle plus que pour sa génération. */
  var epoch = 0;

  function check(mine) {
    window.fetch(CART_URL + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (mine === epoch) { reconcile(cart); }
      })
      .catch(function () { /* leave the UI as-is */ });
  }

  // change = a cart mutation just happened → the gift app may settle late: check a few times.
  // init   = first paint / explicit re-init → one light check is enough.
  function schedule(delays) {
    if (selfDispatch) { return; } // don't react to our own re-dispatch
    clearTimers();
    epoch += 1;
    var mine = epoch;
    delays.forEach(function (d) {
      timers.push(setTimeout(function () { check(mine); }, d));
    });
  }

  /*
   * MESURÉ, PAS SUPPOSÉ. Scénario réel rejoué 4 fois (panier = 1 produit payant + le cadeau
   * auto-ajouté par l'app ; on supprime le produit payant ; /cart.js lu à chaque étape) :
   *
   *   t≈0          POST /cart/change.js — le thème retire la ligne payée
   *   t≈430-520    theme:cart:change item_count=1 → les sondes partent d'ici
   *   t≈730-820    sonde +300  → item_count = 1. L'app n'a PAS encore écrit. Aucun écart. 4 fois sur 4.
   *   t≈840-1430   POST /cart/update.js de free-gift-cart-upsell-pro : le cadeau part côté serveur
   *   t≈1150-1440  l'app émet un theme:cart:reload NU, sans panier : CartItems re-rend avec son état
   *                périmé → liste vide, badge toujours à « 1 ». C'est le blocage décrit en tête de
   *                ce fichier.
   *   t≈1330-1430  sonde +900  → item_count = 0 → écart détecté → réparé (3 fois sur 4)
   *   t≈2430-2640  sonde +2000 → a réparé la 4e fois, où l'app avait écrit 550 ms plus tard
   *
   * La sonde à 300 ms n'a jamais lu un panier postérieur à l'écriture de l'app : elle tombe
   * structurellement avant. C'était une requête sur les six que coûte un changement de quantité, pour
   * zéro observation — elle est retirée. Les deux autres RESTENT : la fenêtre d'écriture de l'app va
   * de 840 à 1430 ms et il faut la couvrir en entier. Ne pas « optimiser » le 2000 au passage.
   */
  document.addEventListener('theme:cart:change', function () { schedule([900, 2000]); });
  document.addEventListener('theme:cart:init', function () { schedule([600]); });
})();
